/**
 * AdminTabs — sticky stats bar + sticky tab navigation strip.
 *
 * Extracted from Admin.jsx (Phase 2 P2 #2 decomposition · 2026-06).
 * Purely presentational: receives stats + current tab + setter +
 * health badge count and renders. No data fetching, no API calls,
 * no business logic.
 *
 * Behaviour byte-identical to the inlined version: same testids
 * (`stats-strip`, `admin-tabs`, `tab-{id}`, `health-badge`), same
 * sticky-offset stack (stats top:72px, tabs top:123px), same active-
 * tab visual treatment (border-b + bg tint), same Rides / Health
 * badge logic (active_rides count + total of stuck + ws-clusters).
 */
import React from "react";
import {
  Users, Car, DollarSign, Activity, TrendingUp, ArrowLeft, LayoutGrid, RotateCcw, Wallet,
} from "lucide-react";
import { StatChip } from "./shared";
import { TABS } from "./tabsConfig";
import { LaunchSafetyBadge } from "./LaunchSafetyBadge";
import { useFinancialPrivacy } from "../../lib/financialPrivacy";
import { FinancialPrivacyToggle } from "../../components/FinancialPrivacyToggle";

// Label/icon lookup for the section bar — built from the canonical TABS list
// plus the synthetic "home" entry so the bar can title any open panel.
const TAB_META = TABS.reduce((acc, t) => { acc[t.id] = t; return acc; }, {});

export const AdminStatsBar = ({ stats }) => {
  const { mask, mode } = useFinancialPrivacy();
  if (!stats) return null;
  const money = (v) => mask(`£${Number(v ?? 0).toFixed(2)}`);
  return (
    <div className="sticky top-[72px] z-20 bg-white border-b border-border" data-testid="stats-strip">
      <div className="flex items-stretch overflow-x-auto gap-px bg-border">
        <StatChip icon={<Activity />}   label="ACTIVE"  value={stats.active_rides} accent="#FFC107" pulse />
        <StatChip icon={<Car />}        label="ONLINE"  value={`${stats.online_drivers}/${stats.total_drivers}`} accent="#10B981" />
        <StatChip icon={<Users />}      label="CUSTOMERS" value={stats.total_passengers} />
        <StatChip icon={<TrendingUp />} label="DONE"    value={stats.completed_rides} />
        <StatChip icon={<DollarSign />} label="GROSS REV." value={money(stats.revenue)} accent="#002FA7" highlight />
        <StatChip icon={<RotateCcw />}  label="REFUNDS" value={money(stats.refunds_total)} accent="#EF4444" />
        <StatChip icon={<Wallet />}     label="NET REV." value={money(stats.net_revenue ?? stats.revenue)} accent="#16A34A" highlight />
        <div className="flex items-center gap-2 bg-white px-3 ml-auto">
          {mode !== "visible" && <FinancialPrivacyToggle />}
          <LaunchSafetyBadge />
        </div>
      </div>
    </div>
  );
};

// Compact section bar — replaces the old flat tab strip once a panel is open.
// Gives a clear "back to all sections" affordance + the current section title,
// keeping the chrome minimal (the grouped home is the primary nav).
export const AdminSectionBar = ({ tab, onHome }) => {
  const meta = TAB_META[tab];
  const Icon = meta?.icon;
  const label = meta?.label || (tab === "onboarding" ? "Setup" : tab);
  return (
    <div className="sticky top-[72px] z-20 bg-white border-b border-border" data-testid="admin-section-bar">
      <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <button
          onClick={onHome}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
          data-testid="nav-home-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          <LayoutGrid className="w-4 h-4" />
          <span className="hidden sm:inline">All sections</span>
        </button>
        <span className="w-px h-4 bg-border" />
        <span className="inline-flex items-center gap-2 font-heading font-black tracking-tight">
          {Icon && <Icon className="w-4 h-4" strokeWidth={2.5} />}
          {label}
        </span>
      </div>
    </div>
  );
};

export const AdminTabNav = ({ tab, setTab, stats, healthBadge, fatigueBadge, fatigueTone, complianceBadge, complianceTone, inboxBadge = 0 }) => (
  <div className="sticky top-[123px] z-20 bg-white border-b border-border" data-testid="admin-tabs">
    {/* Responsive grid: tabs keep a comfortable min width (no squashing of
        long labels like "Subscriptions") and WRAP onto additional rows
        instead of overflowing horizontally. auto-fit keeps every tab the
        same width across rows, so core operational tabs fill the first row
        and the later config tabs (Corporate, Subscriptions, Inbox, System,
        Settings…) flow cleanly onto the next row at laptop/tablet widths. */}
    <div className="grid grid-cols-[repeat(auto-fit,minmax(8.5rem,1fr))]">
      {TABS.map((t) => {
        const Icon = t.icon;
        const active = tab === t.id;
        return (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`py-3 px-2 flex flex-col md:flex-row items-center justify-center gap-1.5 label-mini transition-colors border-b-2 ${
              active
                ? "border-[#0A0A0A] text-foreground bg-secondary/30"
                : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/20"
            }`}
            data-testid={`tab-${t.id}`}
          >
            <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={2.5} />
            <span className="whitespace-nowrap">{t.label}</span>
            {t.id === "rides" && stats?.active_rides > 0 && (
              <span className="bg-[#FFC107] text-black px-1.5 py-0.5 rounded-sm text-[10px] font-bold ml-1">{stats.active_rides}</span>
            )}
            {t.id === "health" && healthBadge > 0 && (
              <span
                className="bg-[#EF4444] text-white px-1.5 py-0.5 rounded-sm text-[10px] font-bold ml-1 animate-pulse"
                data-testid="health-badge"
              >
                {healthBadge}
              </span>
            )}
            {t.id === "fatigue" && fatigueBadge > 0 && (
              <span
                className={`${fatigueTone === "over" ? "bg-[#EF4444] text-white" : "bg-[#F59E0B] text-black"} px-1.5 py-0.5 rounded-sm text-[10px] font-bold ml-1`}
                data-testid="fatigue-badge"
              >
                {fatigueBadge}
              </span>
            )}
            {t.id === "inbox" && inboxBadge > 0 && (
              <span
                className="bg-[#166534] text-white px-1.5 py-0.5 rounded-sm text-[10px] font-bold ml-1"
                data-testid="inbox-badge"
              >
                {inboxBadge}
              </span>
            )}
            {t.id === "compliance" && complianceBadge > 0 && (
              <span
                className={`${complianceTone === "expired" ? "bg-[#EF4444] text-white" : "bg-[#F59E0B] text-black"} px-1.5 py-0.5 rounded-sm text-[10px] font-bold ml-1`}
                data-testid="compliance-badge"
              >
                {complianceBadge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  </div>
);
